import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { fmtDateTimeShort } from "@/lib/dates";
import { Inbox, AlertTriangle, Send, Flag } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function CareTeamInbox() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const load = () => api.get("/care-team/inbox").then(({ data }) => {
    setThreads(data.threads || []);
    setAlerts(data.alerts || []);
    if (!active && (data.threads || []).length) setActive(data.threads[0].service_user_id);
  });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (ref.current) ref.current.scrollIntoView({ behavior: "smooth" }); }, [active, threads]);

  const activeThread = threads.find((t) => t.service_user_id === active);
  const unreplied = activeThread?.messages?.filter((m) => m.from_user_role === "family_member" && !m.first_reply_at) || [];
  const targetMsg = unreplied[0]; // latest unreplied family message

  const sendReply = async (e) => {
    e.preventDefault();
    if (!targetMsg) return;
    setBusy(true);
    try { await api.post(`/family/messages/${targetMsg.id}/reply`, { message: reply }); setReply(""); await load(); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 inline-flex items-center gap-2"><Inbox className="h-6 w-6" /> Care team inbox</h1>
          <p className="text-sm text-slate-600">Family messages by service user. Replies are visible to the family on their portal.</p>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="48h-alerts">
          <p className="text-sm font-semibold text-amber-900 inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {alerts.length} thread{alerts.length === 1 ? "" : "s"} unanswered &gt; 48 hours</p>
          <ul className="mt-1 text-xs text-amber-900 space-y-0.5">
            {alerts.map((a) => (
              <li key={a.service_user_id} className="flex items-center gap-2">
                <button onClick={() => setActive(a.service_user_id)} className="underline">{a.service_user_name}</button>
                <span>· {Math.round(a.age_hours)}h</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="care-team-inbox">
        <aside className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 space-y-1 max-h-[70vh] overflow-y-auto">
          {threads.map((t) => {
            const lastFamily = [...(t.messages || [])].reverse().find((m) => m.from_user_role === "family_member");
            const flagged = (t.messages || []).some((m) => m.auto_flagged);
            return (
              <button key={t.service_user_id} onClick={() => setActive(t.service_user_id)} data-testid={`inbox-thread-${t.service_user_id}`}
                className={`w-full text-left px-3 py-2 rounded ${active === t.service_user_id ? "bg-[#E0F2F2] text-[#0D7377]" : "hover:bg-slate-50"}`}>
                <p className="text-sm font-medium flex items-center gap-1.5">{flagged && <Flag className="h-3 w-3 text-red-600" />} {t.service_user_name}</p>
                <p className="text-xs text-slate-500 line-clamp-1">{lastFamily?.message || "—"}</p>
              </button>
            );
          })}
          {threads.length === 0 && <p className="text-sm text-slate-500 px-3 py-4">No threads.</p>}
        </aside>
        <div className="md:col-span-2 bg-white rounded-lg border border-slate-200 shadow-sm flex flex-col h-[70vh]">
          <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="inbox-message-list">
            {(activeThread?.messages || []).map((m) => (
              <div key={m.id} className={`max-w-md ${m.from_user_role === "family_member" ? "bg-slate-100 text-slate-800" : "ml-auto bg-[#0D7377] text-white"} rounded-lg px-3 py-2`}>
                {m.auto_flagged && <p className="text-[10px] font-semibold inline-flex items-center gap-1 mb-1 text-red-700"><AlertTriangle className="h-3 w-3" /> Auto-flagged: {(m.flagged_keywords || []).join(", ")}</p>}
                {m.routing === "concern" && <p className="text-[10px] uppercase font-semibold mb-1">Concern (mgmt routing)</p>}
                <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                <p className={`text-[10px] mt-1 ${m.from_user_role === "family_member" ? "text-slate-500" : "text-white/80"}`}>{m.from_name} · {fmtDateTimeShort(m.created_at)}</p>
                {(m.replies || []).map((r) => (
                  <div key={r.id} className={`mt-2 pt-2 border-t ${m.from_user_role === "family_member" ? "border-slate-300" : "border-white/30"} text-sm`}>
                    <p className="text-[10px] font-semibold opacity-80">↪ {r.from_name} ({r.from_user_role.replace(/_/g, " ")})</p>
                    <p>{r.message}</p>
                  </div>
                ))}
              </div>
            ))}
            <div ref={ref} />
            {!activeThread?.messages?.length && <p className="text-sm text-slate-500 text-center py-8">Pick a thread to start.</p>}
          </div>
          {targetMsg && (
            <form onSubmit={sendReply} className="border-t border-slate-200 p-3 flex gap-2" data-testid="inbox-reply-form">
              <textarea required value={reply} onChange={(e) => setReply(e.target.value)} rows={2} placeholder="Reply to family…" className="flex-1 px-3 py-2 border border-slate-300 rounded-md" data-testid="inbox-reply-input" />
              <button disabled={busy} data-testid="inbox-reply-send" className="bg-[#0D7377] hover:bg-[#0A5C5F] text-white px-4 py-2 rounded-md font-medium inline-flex items-center gap-2 self-end">
                <Send className="h-4 w-4" /> Reply
              </button>
            </form>
          )}
          {!targetMsg && activeThread && (
            <div className="border-t border-slate-200 p-3 text-xs text-slate-500">All family messages have been replied to.</div>
          )}
        </div>
      </div>
    </div>
  );
}
